import { SourceType } from 'module/api/filter/types';
import { useTranslate } from 'module/hooks/use-translation';
import { FieldType } from 'module/types/filter-field/field/field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { SignalType } from 'api/signal/types';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { useMemo } from 'react';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';
import { useReportingUsersField } from './reporting-users';
import { useSignalCompanyNamesField } from './signal-company-names';

export const useSignalNativeFieldsAsFilterableFields = () => {
  const translate = useTranslate();
  const { data: signalConfigurations, isLoading: signalConfigurationsLoading } =
    useGetAllSignalConfigurationsQuery();

  const signalConfigurationOptions = useMemo(
    () =>
      (signalConfigurations ?? []).map((signal) => {
        const signalTypeConfig =
          signalTypeConfigs[signal.signalType as SignalType];

        return {
          label: signal.signalName,
          value: signal.signalConfigurationId,
          active: true,
          color: signalTypeConfig?.color || 'default',
        };
      }),
    [signalConfigurations],
  );

  const signalTypeOptions = useMemo(() => {
    const uniqueSignalTypes = [
      ...new Set(
        signalConfigurations?.map((signal) => signal.signalType) || [],
      ),
    ];

    return uniqueSignalTypes.map((signalType) => {
      const signalTypeConfig = signalTypeConfigs[signalType as SignalType];

      return {
        label: signalType,
        value: signalType.toString(),
        active: true,
        color: signalTypeConfig?.color || 'default',
      };
    });
  }, [signalConfigurations]);

  const { field: saidByField, isLoading: saidByLoading } =
    useReportingUsersField(-5);

  const { field: companyField, isLoading: companyLoading } =
    useSignalCompanyNamesField(-3);

  const fields = useMemo(
    () =>
      [
        {
          fieldId: -1,
          fieldName: 'signalConfigurationId',
          fieldLabel: translate('signal.fields.signal'),
          type: FieldType.MultiSelect,
          sourceType: SourceType.Signal,
          customField: false,
          private: false,
          isEnabled: true,
          userCreated: false,
          metaData: {
            required: false,
            options: signalConfigurationOptions,
          },
        },
        companyField,
        saidByField,
        {
          fieldId: -7,
          fieldName: 'createdAt',
          fieldLabel: translate('signal.fields.created_on'),
          type: FieldType.Date,
          sourceType: SourceType.Signal,
          customField: false,
          private: false,
          isEnabled: true,
          userCreated: false,
          metaData: {
            required: false,
          },
        },
        {
          fieldId: -8,
          fieldName: 'signalType',
          fieldLabel: translate('signal.fields.signal_type'),
          type: FieldType.SingleSelect,
          sourceType: SourceType.Signal,
          customField: false,
          private: false,
          isEnabled: true,
          userCreated: false,
          metaData: {
            required: false,
            options: signalTypeOptions,
          },
        },
      ].map((x) => x as FilterableField),
    [
      signalConfigurationOptions,
      signalTypeOptions,
      saidByField,
      companyField,
      translate,
    ],
  );

  return useMemo(
    () => ({
      loading: signalConfigurationsLoading || saidByLoading || companyLoading,
      fields,
    }),
    [signalConfigurationsLoading, saidByLoading, companyLoading, fields],
  );
};
